import {
	Button,
	CloseIcon,
	ErrorIcon,
	InfoIcon,
	SuccessIcon,
	WarningIcon,
} from '@gurupay/gp-ui-library';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

export interface AlertProps {
	id?: string;
	children?: ReactNode;
	type?: 'info' | 'success' | 'warning' | 'error' | 'announcement';
	className?: string;
	canClose?: boolean;
}

const alertConfig = {
	info: {
		icon: <InfoIcon className="text-status-info-500" />,
		color: 'bg-status-info-50 border-status-info-300',
	},
	success: {
		icon: <SuccessIcon className="text-status-success-500" />,
		color: 'bg-status-success-50 border-status-success-300',
	},
	error: {
		icon: <ErrorIcon className="text-status-error-500" />,
		color: 'bg-status-error-50 border-status-error-300',
	},
	announcement: {
		icon: <ErrorIcon className="text-status-error-500" />,
		color: 'bg-status-error-50 border-status-error-300',
	},
	warning: {
		icon: <WarningIcon className="text-status-warning-500" />,
		color: 'bg-status-warning-50 border-status-warning-300',
	},
};

const Alert = ({ id, type, className, children, canClose }: AlertProps) => {
	const [isClosing, setIsClosing] = useState(false);

	const handleClose = () => {
		sessionStorage.setItem(`alert_closed_${id}`, 'true');
		setIsClosing(true);
	};

	useEffect(() => {
		const isClosed = sessionStorage.getItem(`alert_closed_${id}`);
		if (isClosed === 'true') {
			setIsClosing(true);
		}
	}, [id]);

	return (
		<div
			className={clsx(
				type && alertConfig[type]?.color,
				className,
				'text-neutral-950 flex text-sm-medium pl-space-8 pr-space-16 gap-space-8 rounded-lg border w-full md:w-[500px]',
				'overflow-hidden transition-all duration-500 ease-in-out transform-gpu',
				isClosing
					? 'opacity-0 max-h-0 mb-0 -translate-y-2 pointer-events-none h-0 py-0'
					: 'opacity-100 max-h-[500px] translate-y-0 h-auto py-space-16',
			)}
		>
			<span>{type && alertConfig[type]?.icon}</span>
			<div className="md:w-full w-4/5">{children}</div>
			{canClose && (
				<Button
					aria-label="Close alert"
					className="bg-transparent !h-[24px] w-[24px] !px-0 !py-0 items-center"
					onClick={handleClose}
					variant="ghost"
				>
					<CloseIcon />
				</Button>
			)}
		</div>
	);
};

export default Alert;
